<template>
  <div class="cat-material">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-material-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${materiales.length} Materiales`"
          @new="addMaterial"
          @refresh="loadMateriales"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando Materiales...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!materiales || materiales.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay materiales registrados. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>
        
        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && materiales && materiales.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="materiales"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar Materiales..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editMaterial(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar Material"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.Clave)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar Material"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Material -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="materialModal" 
      tabindex="-1" 
      aria-labelledby="materialModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="materialModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Material' : 'Nuevo Material' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatMaterialForm
              :material="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatMaterialService from '../controllers/CatMaterialController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatMaterialForm from '../components/CatMaterialForm.vue';

export default {
  name: 'CatMaterialView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatMaterialForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: new CatMaterialService(),
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: null,
        Nombre: '',
        Descripcion: '',
        ClaveErp: '',
        Umedida: '',
        GrupoMaterial: '',
        CuentaContable: '',
        TipoArticulo: '',
        EsInventariable: false,
        EsDeCompra: false,
        EsDeVenta: false,
        EsActivo: true,
        UltimoPrecioCompra: 0,
        PrecioPromedio: 0,
        PrecioUltimo: 0,
        PrecioMinimo: 0,
        PrecioMaximo: 0,
        Impuesto1: '',
        Impuesto2: '',
        Impuesto3: '',
        CuentaContableCosto: '',
        CuentaContableVenta: '',
        CuentaContableGasto: ''
      }
    };
  },
  computed: {
    materiales() {
      return this.service.materiales;
    },
    loading() {
      return this.service.loading;
    },
    error() {
      return this.service.error;
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'Clave', label: 'Clave', sortable: true },
        { key: 'EmpresaId', label: 'Empresa ID', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'Descripcion', label: 'Descripción', sortable: true },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
        { key: 'Umedida', label: 'Unidad', sortable: true },
        { key: 'GrupoMaterial', label: 'Grupo', sortable: true },
        { key: 'CuentaContable', label: 'Cuenta Contable', sortable: true },
        { key: 'TipoArticulo', label: 'Tipo', sortable: true },
        { key: 'EsInventariable', label: 'Inventariable', sortable: true },
        { key: 'EsDeCompra', label: 'Compra', sortable: true },
        { key: 'EsDeVenta', label: 'Venta', sortable: true },
        { key: 'EsActivo', label: 'Activo', sortable: true },
        { key: 'UltimoPrecioCompra', label: 'Último Precio', sortable: true },
        { key: 'PrecioPromedio', label: 'Precio Promedio', sortable: true },
        { key: 'PrecioUltimo', label: 'Precio Último', sortable: true },
        { key: 'PrecioMinimo', label: 'Precio Mínimo', sortable: true },
        { key: 'PrecioMaximo', label: 'Precio Máximo', sortable: true },
        { key: 'Impuesto1', label: 'Impuesto 1', sortable: true },
        { key: 'Impuesto2', label: 'Impuesto 2', sortable: true },
        { key: 'Impuesto3', label: 'Impuesto 3', sortable: true },
        { key: 'CuentaContableCosto', label: 'Cuenta Costo', sortable: true },
        { key: 'CuentaContableVenta', label: 'Cuenta Venta', sortable: true },
        { key: 'CuentaContableGasto', label: 'Cuenta Gasto', sortable: true }
      ];
    },
    // Verificar si hay empresa seleccionada
    hasEmpresa() {
      return this.service ? this.service.hasEmpresa : false;
    }
  },
  created() {
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadMateriales() {
      console.log('Cargando Materiales...');
      await this.service.listar();
    },
    addMaterial() {
      this.isEditing = false;
      this.openModal();
    },
    async editMaterial(material) {
      this.isEditing = true;
      try {
        // Cargar datos completos del Material
        const response = await this.service.lectura(material.Clave);
        if (response && response.data) {
          this.formData = { ...response.data };
        } else {
          this.formData = { ...material };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar Material:', error);
        this.$alert.error('No se pudo cargar el Material para edición.');
      }
    },
    confirmDelete(clave) {
      console.log('Iniciando confirmación de eliminación para:', clave);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el Material "${clave}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteMaterial(clave);
        }
      });
    },
    async deleteMaterial(clave) {
      console.log('Ejecutando eliminación para:', clave);
      
      try {
        const response = await this.service.borrar({ Clave: clave });
        console.log('Respuesta de eliminación recibida:', response);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Material eliminado exitosamente');
          // Recargar datos
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo eliminar el Material. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar Material:', error);
        this.$alert.error('No se pudo eliminar el Material. Por favor, intente nuevamente.');
      }
    },
    async downloadBulk() {
      try {
        await this.service.downloadBulk();
        this.$alert.success('Descarga iniciada correctamente');
      } catch (error) {
        this.$alert.error('Error al descargar: ' + error.message);
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: null,
        Nombre: '',
        Descripcion: '',
        ClaveErp: '',
        Umedida: '',
        GrupoMaterial: '',
        CuentaContable: '',
        TipoArticulo: '',
        EsInventariable: false,
        EsDeCompra: false,
        EsDeVenta: false,
        EsActivo: true,
        UltimoPrecioCompra: 0,
        PrecioPromedio: 0,
        PrecioUltimo: 0,
        PrecioMinimo: 0,
        PrecioMaximo: 0,
        Impuesto1: '',
        Impuesto2: '',
        Impuesto3: '',
        CuentaContableCosto: '',
        CuentaContableVenta: '',
        CuentaContableGasto: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        const response = await this.service.guardar(formData);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Material guardado exitosamente');
          // Cerrar modal y recargar datos
          this.closeModal();
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo guardar el Material. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al guardar Material:', error);
        this.$alert.error('No se pudo guardar el Material. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.cat-material {
  min-height: calc(100vh - 200px);
}

.cat-material-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 1200px;
  width: 95%;
  z-index: 1055;
  max-height: 95vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 98% !important;
    max-width: none !important;
    max-height: 98vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 99% !important;
    max-height: 99vh;
  }
}
</style>